// types
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { FeeOverride, paymentServicesTypes } from '@makemydeal/dr-platform-types';
import { FeesCategoriesType } from '@makemydeal/dr-shared-types';
import { createSelector } from 'reselect';
import { getCapitalizedFeesByOfferType, getCurrentOfferType, getItemizedLenderFees, getTotalFee } from '../selectors/offerRedux';
import { getItemizedDealerFees, getItemizedGovernmentFees } from '../selectors/offerSelectors';
import type { CustomFeeItem, DealerFeeWithIncludeCal, GovernmentFeeWithIncludeCal } from '../types/types';

const isGovernmentFees = (
    fee: DealerFeeWithIncludeCal | GovernmentFeeWithIncludeCal,
    category: paymentServicesTypes.FeeCategory
): fee is GovernmentFeeWithIncludeCal => {
    return category === FeesCategoriesType.GOVERNMENT;
};

export const getCustomFeeItemsAdapter = (
    feeItem: (DealerFeeWithIncludeCal | GovernmentFeeWithIncludeCal)[],
    categoryType: paymentServicesTypes.FeeCategory
): CustomFeeItem[] => {
    return feeItem?.map((fee) => {
        if (isGovernmentFees(fee, categoryType)) {
            return {
                feeTypeId: fee.code,
                feeAmount: fee.amount.toString(),
                capIndicator: fee.capped,
                feeDescription: fee.description,
                feeName: fee.name || fee.description,
                category: categoryType,
                isManualFee: fee.isManualFee,
                includedInCalc: fee.isFeeIncludedInCalculation ?? fee.includedInCalc ?? true,
                isDeleted: fee.isManualFee && fee.amount === 0,
                isEdited: fee.isManualFee && fee.amount > 0
            };
        }

        return {
            feeTypeId: fee.dealerFeeTypeCode,
            feeAmount: fee.dealerFeeAmount.toString(),
            capIndicator: fee.dealerFeeCapped,
            feeDescription: fee.dealerFeeDesc,
            feeName: fee.dealerFeeName || fee.dealerFeeDesc,
            category: categoryType,
            includedInCalc: fee.isFeeIncludedInCalculation ?? fee.includedInCalc ?? true,
            isManualFee: fee.isManualFee,
            isDeleted: fee.isManualFee && fee.dealerFeeAmount === 0,
            isEdited: fee.isManualFee && fee.dealerFeeAmount > 0
        };
    });
};

const getAllNormalizedFees = createSelector(
    [getItemizedGovernmentFees, getItemizedDealerFees, (state) => getItemizedLenderFees(state)],
    (govFees, dealerFees, lenderFees): CustomFeeItem[] => {
        const govFeeItems = getCustomFeeItemsAdapter(govFees, FeesCategoriesType.GOVERNMENT) ?? [];
        const dealerFeeItems = getCustomFeeItemsAdapter(dealerFees, FeesCategoriesType.DEALER) ?? [];
        const lenderFeeItems = getCustomFeeItemsAdapter(lenderFees, FeesCategoriesType.LENDER) ?? [];

        return [...govFeeItems, ...dealerFeeItems, ...lenderFeeItems];
    }
);

export const mergeAllFeeTypes = createSelector([getAllNormalizedFees], (updatedFees): FeeOverride[] => {
    const mergedFees = mergeFees(updatedFees);

    return mergedFees.map<FeeOverride>((fee) => ({
        category: fee.category,
        feeAmount: Number(fee.feeAmount),
        feeDescription: fee.feeDescription,
        feeName: fee.feeName,
        feeTypeId: Number(fee.feeTypeId),
        includeInCalcIndicator: fee.includedInCalc,
        reqCapIndicator: true
    }));
});

const getMemoizedMergedFees = createSelector([getAllNormalizedFees], (normalizedFees): CustomFeeItem[] =>
    mergeFees(normalizedFees)
);

export const updateFeesWithCapIndicator = createSelector(
    [getMemoizedMergedFees, (_state: any, capitalized: boolean = false) => capitalized],
    (mergedFees, capitalized): FeeOverride[] => {
        return mergedFees.map<FeeOverride>((fee) => ({
            category: fee.category,
            feeAmount: Number(fee.feeAmount),
            feeDescription: fee.feeDescription,
            feeName: fee.feeName,
            feeTypeId: fee.feeTypeId,
            includeInCalcIndicator: fee.includedInCalc,
            reqCapIndicator: capitalized
        }));
    }
);

export const isAllFeesCapitalized = createSelector([getMemoizedMergedFees], (mergedFees): boolean => {
    return Boolean(mergedFees.every((fee) => fee.capIndicator));
});

export const isTermAllFeesCapitalized = (state: StateTree): boolean => {
    const offerType = getCurrentOfferType(state);
    const capitalizedTotal = getCapitalizedFeesByOfferType(state, offerType);
    const feesTotal = getTotalFee(state, offerType);

    return Boolean(feesTotal) && feesTotal === capitalizedTotal;
};

export const mergeFees = (fees: CustomFeeItem[]): CustomFeeItem[] => {
    const merged: CustomFeeItem[] = [];

    for (const fee of fees) {
        const existingIndex = merged.findIndex((item) => item.feeTypeId === fee.feeTypeId && item.isDeleted);
        if (existingIndex > -1) {
            merged[existingIndex] = fee;
        } else {
            merged.push(fee);
        }
    }

    return merged;
};
